'use client';
import {
    FC,
    ReactNode,
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
} from 'react';
import {
    AuthContextType,
    clearAuth,
    getAuth,
    storeAuth,
    // TokenCreateParam,
    // TokenCreateResult,
} from '@/lib/token-utils';
import { UserCurrent } from '@/services/personal-center/types';
import { useRouter } from 'next/navigation';
import { storeProfile, getProfile, clearProfile } from '@/lib/profile-utils';
import {
    TokenCreateResult,
    TokenCreateParam,
} from '@/services/personal-center/types';
import { getUsersCurrent } from '@/services/personal-center';
import { loginApi, logoutApi } from '@/services/personal-center';
import {
    initializeRefreshTimer,
    hasAuthToken,
    cleanupTimer,
} from '@/lib/token-refresh';

// 刷新令牌方法使用token-refresh.ts中的功能

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [tokenCreateResult, setTokenCreateResult] =
        useState<TokenCreateResult | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [profile, setProfile] = useState<UserCurrent | null>(null);
    const router = useRouter();

    const login = async (
        params: TokenCreateParam,
    ): Promise<{ success: boolean; message: string }> => {
        setIsLoading(true);

        try {
            const response = await loginApi(params);

            console.log('Login result:', response);

            if (response?.success) {
                storeAuth(response.result);
                setTokenCreateResult(response.result);

                // 登录成功后获取用户profile
                const profileResponse = await getUsersCurrent();
                if (profileResponse?.success) {
                    storeProfile(profileResponse.result);
                    setProfile(profileResponse.result);
                    console.log('User profile fetched and stored successfully');
                } else {
                    console.warn('Failed to fetch user profile after login');
                }

                // 登录成功后立即初始化token刷新定时器
                console.log(
                    'Login successful, initializing token refresh timer',
                );
                initializeRefreshTimer();

                return {
                    success: true,
                    message: response?.message || '登录成功',
                };
            }
            return {
                success: false,
                message: response?.message ? response.message : '登录失败',
            };
        } catch (error) {
            return {
                success: false,
                message: error instanceof Error ? error.message : '登录失败',
            };
        } finally {
            setIsLoading(false);
        }
    };

    const refreshProfile = useCallback(async (): Promise<boolean> => {
        try {
            const profileResponse = await getUsersCurrent();
            if (profileResponse?.success) {
                storeProfile(profileResponse.result);
                setProfile(profileResponse.result);
                console.log('User profile refreshed successfully');
                return true;
            } else {
                console.warn('Failed to refresh user profile');
                return false;
            }
        } catch (error) {
            console.error('Refresh profile error:', error);
            return false;
        }
    }, []);

    // 封装认证状态重置逻辑
    const resetAuthState = useCallback(() => {
        setTokenCreateResult(null);
        clearAuth();
        clearProfile();
        setProfile(null);
    }, []);

    const logout = async () => {
        try {
            const response = await logoutApi();
            if (response && response.success) {
                resetAuthState();
                router.push('/login');
                return true;
            } else {
                return false;
            }
        } catch (error) {
            return false;
        }
    };

    // Token刷新相关方法已移至token-refresh.ts文件

    useEffect(() => {
        const initAuth = async () => {
            const storedTokenCreateResult = getAuth();
            console.log('Stored auth:', storedTokenCreateResult);
            // 如果localStorage中没有数据，检查cookie中是否有refresh_token，这里刷新token用的是refresh_token
            if (!storedTokenCreateResult) {
                // 屏蔽localStorage中没有数据，cookie有数据时刷新token的逻辑，直接跳转到登录页
                resetAuthState();
                setIsLoading(false);
                return;
            }

            // 设置认证状态
            setTokenCreateResult(storedTokenCreateResult);
            // 检查是否有profile，如果没有则尝试获取
            const existingProfile = getProfile();
            if (!existingProfile) {
                console.log('No existing profile found, attempting to fetch');
                try {
                    const profileResponse = await getUsersCurrent();
                    if (profileResponse?.success) {
                        storeProfile(profileResponse.result);
                        console.log(
                            'User profile fetched successfully during init',
                        );
                    }
                } catch (error) {
                    console.error('获取用户信息失败:', error);
                }
            }

            setProfile(getProfile());
            setIsLoading(false);

            console.log('AuthContext value:', {
                tokenCreateResult,
                isLoading,
                profile,
            });
        };
        // 调用initAuth函数来初始化认证状态
        initAuth();
        console.log('Auth state initialized');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetAuthState]);

    // 页面刷新时，如果已有认证信息，初始化定时器
    useEffect(() => {
        // 只有在有认证token时初始化定时器
        if (hasAuthToken()) {
            console.log('Initializing token refresh timer with auth token');
            initializeRefreshTimer();
        }
        // 组件卸载时清理
        return () => {
            cleanupTimer();
            console.log('Token refresh timer cleanup completed');
        };
    }, []);

    return (
        <AuthContext.Provider
            value={{
                tokenCreateResult,
                login,
                logout,
                refreshProfile,
                resetAuthState,
                isLoading,
                profile,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
